import React, { useState, useRef } from "react";
import { Container } from "@material-ui/core";
import { CallToActionButton } from "../../atoms/callToActionButton/CallToActionButton";
import TableOrganism from "../../organisms/Table/TableOrganism";
import { createHeaderRow } from "../../utilities/CreateHeaderRow";
import {
  useStyles,
  ButtonWrapper,
  ButtonsWrapper,
} from "./AddItemPageStyles";
import { TableRowProps } from "../../molecules/TableRow/TableRowAtom";
import { createItemsRows } from "./AddItemTable";
import { RouteComponentProps, useHistory, useLocation, useParams } from "react-router-dom";
import IPage from "../../config/interfaces/page";
import {
  BigRedButton,
  ButtonVersion,
} from "../../atoms/BigRedButton/BigRedButtonAtom";
import Loader from "../../atoms/Loader/LoaderAtom";
import useApiService from "../../utilities/useApiService";
import { useAuthState } from "../../context/AuthContext";
import { isRoleIncluded } from "./../../utilities/checkUserRole";
import { ReturnRequestServiceType, UserRole } from "../../utilities/Enums";
import { ItemsTable } from "../../network/models/TableInterfaces";
import PrintHeaderInfo from "../../organisms/PrintHeaderInfo/PrintHeaderInfo";
import { usePharmacyInfo } from "../../hooks/usePharmacyInfo";
import { useFetchRequestItems } from "../../hooks/useFetchRequestItems";
import { useReturnRequest } from "../../hooks/useReturnRequest";
import { useDeleteRequestItem } from "../../hooks/useDeleteItem";
import { ReturnItemForm } from "../../molecules/ReturnItemForm/ReturnItemForm";
import AppBreadcrumbs from "../../organisms/Breadcrumbs/Breadcrumbs";

export interface AddItemPageProps { }

export const AddItemPage: React.FC<
  AddItemPageProps &
  RouteComponentProps<{}, {}, { serviceType: ReturnRequestServiceType }> &
  IPage
> = (props) => {
  const history = useHistory();
  const params = useParams<{ pharmacyId: string; requestId: string }>();
  const { putMethod } = useApiService();

  const [editingItem, setEditingItem] = useState<ItemsTable | undefined>(undefined);
  const formRef = useRef<HTMLDivElement>(null);

  const classes = useStyles(props);
  const { user } = useAuthState();
  const [items, setItems, itemsState] = useFetchRequestItems(
    params.pharmacyId,
    params.requestId
  );
  const [setDeletedItem] = useDeleteRequestItem(
    params.pharmacyId,
    params.requestId,
    { onSuccess: () => setItems() }
  );
  const [returnRequest, , returnRequestState] =
    useReturnRequest(params.pharmacyId, params.requestId, [
      UserRole.Admin,
      UserRole.Warehouse,
      UserRole.FieldProcessor,
    ]);
  const [pharmacyInfo] = usePharmacyInfo(params.pharmacyId);
  const location = useLocation();

  const isAdminRoute = location.pathname.startsWith("/admin");

  const basePath = isAdminRoute ? "/admin/pharmacies" : "/pharmacies";

  const headerTitles = [
    "NDC",
    "Description",
    "Manufacturer",
    "Strength",
    "Dosage",
    "Lot #",
    "Exp Date",
    "Pkg Size",
    //"GTIN",
    //"Serial #",
    "CSC",
    "Full Qty",
    "Partial Qty",
    "Estimated Price",
    "  ",
    "  ",
  ];

  const handleEditItem = (item: any) => {
    setEditingItem(item);

    if (formRef.current) {
      formRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  const rows: Array<TableRowProps> = items
    ? createItemsRows(items, setDeletedItem, returnRequest?.returnRequestStatus, handleEditItem)
    : [];

  const send = async () => {
    history.push(
      `/pharmacies/${params.pharmacyId}/returnrequests/${params.requestId}/items`
    );
  };

  const handleClickOnCreateShipment = async () => {
    await putMethod(
      `/pharmacies/${params.pharmacyId}/returnrequests/${params.requestId}/send`
    );
    history.push(
      `/pharmacies/${params.pharmacyId}/returnrequests/${params.requestId}/shipments/create`
    );
  };

const pharmacyName = pharmacyInfo?.pharmacy?.doingBusinessAs || "Pharmacy";

const breadcrumbItems = [
  {
    label: pharmacyName,
    path: `${basePath}/${params.pharmacyId}`,
  },
  {
    label: "Pharmacy Returns",
    path: `${basePath}/${params.pharmacyId}/returns`,
  },
  {
    label: "Add Items",
  },
];

  return returnRequestState.isLoading ? (
    <Loader />
  ) : (
    <div>
      <Container>
        {pharmacyInfo && (
          <PrintHeaderInfo
            pharmacyName={pharmacyInfo.pharmacy.doingBusinessAs}
          />
        )}
      <div className={classes.breadcrumbsContainer}>
        <AppBreadcrumbs items={breadcrumbItems} />
      </div>
        {returnRequest?.returnRequestStatus !== "SENT" && (
          <ReturnItemForm
            formRef={formRef}
            pharmacyId={params.pharmacyId}
            requestId={params.requestId}
            locationState={props.location.state}
            onAdd={() => setItems()}
            onUpdate={() => setEditingItem(undefined)}
            editingItem={editingItem}
          />
        )}
        {rows.length > 0 && (
          <div className={classes.addItemPageTableWrapper}>
            <TableOrganism
              header={
                returnRequest?.returnRequestStatus === "DRAFT" ||
                returnRequest?.returnRequestStatus !== "SENT"
                  ? createHeaderRow(headerTitles)
                  : createHeaderRow(
                      headerTitles.slice(0, headerTitles.length - 1),
                    )
              }
              rows={rows}
            />
            {returnRequest?.returnRequestStatus !== "SENT" && (
              <ButtonsWrapper>
                <ButtonWrapper className={classes.marginInBetween}>
                  <div className={classes.customSubmitButton}>
                    <BigRedButton
                      version={ButtonVersion.SMALL}
                      text={"Submit"}
                      type=""
                      onClick={send}
                    />
                  </div>
                </ButtonWrapper>
              </ButtonsWrapper>
            )}
          </div>
        )}
        {itemsState.isFetching ? <Loader /> : null}
        <div className={classes.buttonWrapper2}>
          <CallToActionButton
            height={"70px"}
            text={"Create UPS Label"}
            width={"41%"}
            backgroundStyle="transparent linear-gradient(90deg, #323A87 0%, #211D57 100%) 0% 0% no-repeat padding-box"
            onClick={handleClickOnCreateShipment}
            disabled={isRoleIncluded([UserRole.Warehouse], user?.role)}
          />
        </div>
      </Container>
    </div>
  );
};